/**
 * Plan-usage read (DNKADM-0069) — the per-axis usage + plan cap the three v2 Add
 * buttons (locations / staff / resources) gate on, so a capped Add opens the
 * upgrade popup BEFORE the user fills a doomed form (instead of only after the
 * create is rejected, DNKADM-0065).
 *
 * Source: `GET /v2/admin/plan-usage` (the plan-usage hand-off,
 * `docs/design/HANDOFF-dink-api-plan-usage-endpoint.md`), an OWNER/ADMIN,
 * tenant-from-JWT read on the bearer-only `/v2/admin/**` surface — `request()`
 * routes it (bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry).
 * A GET, so it carries no `X-Session-Mode` audit stamp.
 *
 * **Forward-compatible by design (Stop Conditions).** This endpoint may not be
 * live yet. `getPlanUsage()` throws a typed `ApiError` on a non-2xx (the screens
 * degrade: Add opens the form and the DNKADM-0065 submit-time `PLAN_LIMIT_REACHED`
 * rejection stays the gate), and a 200 that parses empty yields an empty
 * `PlanUsage` whose `isAtCap` is `false` everywhere (same degrade). The button
 * gate NEVER falsely blocks an unknown/unlimited axis — it blocks only a confirmed
 * `current >= limit`. The server stays the real enforcer (DNKAPI-0139).
 *
 * **Unlimited sentinel.** An axis with no cap uses an agreed sentinel — `limit:
 * null` (preferred) or `limit: -1` (the hand-off's two confirm-candidates). BOTH
 * normalize here to "no cap" (`limit: null`), and any other non-finite/negative
 * `limit` degrades the same way, so an unlimited axis is never gated.
 *
 * The wire shape mirrors the DNKAPI-0139 rejection body's semantics
 * (`limit`/`current`/`plan`; `current` counts ACTIVE records — a delete frees a
 * slot). Parsed DEFENSIVELY (mirrors `locations.ts` / `tenantSettings.ts`): a
 * missing axis is OMITTED, a non-numeric `current` drops that axis (treated as
 * unknown → never gate), and a missing/odd `plan` degrades to `null` — never a
 * throw on a malformed-but-2xx body.
 */
import { request } from './client';
import type { PlanLimitInfo } from './v2Errors';

/** v2 admin plan-usage path (bearer surface). */
const PATH = '/v2/admin/plan-usage';

/** The three plan-limited axes (matching the DNKAPI-0139 `axis` values). */
export type PlanAxis = 'locations' | 'staff' | 'resources';

/**
 * Per-axis usage. `current` is the active-record count (a finite number — a row we
 * couldn't read a count for is dropped at parse time). `limit` is the plan cap, or
 * `null` for an UNLIMITED axis (the `null`/`-1` sentinel and any other non-cap
 * value normalize to `null`).
 */
interface AxisUsage {
  limit: number | null;
  current: number;
}

/**
 * The tenant's plan usage. `plan` is the plan name (or `null` when none is
 * assigned). `axes` carries ONLY the axes the server returned a usable row for — a
 * missing axis is omitted (and therefore never gates, via {@link isAtCap}).
 */
export interface PlanUsage {
  plan: string | null;
  axes: {
    locations?: AxisUsage;
    staff?: AxisUsage;
    resources?: AxisUsage;
  };
}

// --- Defensive parsing helpers (mirrors locations.ts / tenantSettings.ts) -------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A finite number, or null (`NaN`/`Infinity`/non-number degrade). */
function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Parse a `limit` to a real cap or `null` ("no cap"). A finite number `>= 0` is a
 * real cap (including `0` — "no slots"); the unlimited sentinel (`null` or `-1`),
 * any other negative, and any non-finite/non-number all normalize to `null` so an
 * unlimited/unknown axis is NEVER gated.
 */
function parseLimit(value: unknown): number | null {
  const num = asNumber(value);
  return num !== null && num >= 0 ? num : null;
}

/**
 * Parse one axis row, or `null` when it has no usable `current` count (a row with
 * no readable count is dropped → that axis stays "unknown" and never gates). A
 * malformed `limit` degrades to "no cap" (see {@link parseLimit}).
 */
function parseAxis(raw: unknown): AxisUsage | null {
  if (!isObject(raw)) return null;
  const current = asNumber(raw['current']);
  if (current === null) return null;
  return { limit: parseLimit(raw['limit']), current };
}

/**
 * Assign a parsed axis onto `target` only when it parsed — keeps the optional key
 * absent (not `undefined`) per `exactOptionalPropertyTypes`, so a missing axis is
 * omitted.
 */
function assignAxis(target: PlanUsage['axes'], axis: PlanAxis, raw: unknown): void {
  const parsed = parseAxis(raw);
  if (parsed !== null) target[axis] = parsed;
}

/**
 * `GET /v2/admin/plan-usage` → the tenant's per-axis plan usage, defensively
 * parsed. A missing/odd `plan` → `null`; a missing or unreadable axis is omitted;
 * an unlimited/unknown `limit` → `null` ("no cap"). A 2xx with an unexpected body
 * yields an empty `PlanUsage` (no axes — nothing gates). Throws a typed `ApiError`
 * on a non-2xx (the screens degrade to the submit-time gate).
 */
export async function getPlanUsage(): Promise<PlanUsage> {
  const body = await request<unknown>('GET', PATH);
  const axesRaw = isObject(body) && isObject(body['axes']) ? body['axes'] : {};
  const axes: PlanUsage['axes'] = {};
  assignAxis(axes, 'locations', axesRaw['locations']);
  assignAxis(axes, 'staff', axesRaw['staff']);
  assignAxis(axes, 'resources', axesRaw['resources']);
  return {
    plan: isObject(body) ? asText(body['plan']) : null,
    axes,
  };
}

/**
 * Is `axis` at its plan cap? `true` ONLY when the axis is PRESENT, its `limit` is a
 * real finite cap (NOT the unlimited `null`), AND `current >= limit`. `false` for
 * an unlimited / absent / unknown axis (and for `null` usage) — so the button gate
 * never falsely blocks. This is the only condition that routes Add to the popup.
 */
export function isAtCap(usage: PlanUsage | null, axis: PlanAxis): boolean {
  const row = usage?.axes[axis];
  if (!row || row.limit === null) return false;
  return row.current >= row.limit;
}

/**
 * The {@link PlanLimitInfo} to drive the upgrade popup when `axis` is at cap, or
 * `null` when it is not (so the caller opens the create form instead). Built from
 * the usage row + plan name, matching the DNKADM-0065 popup contract the
 * submit-time rejection feeds — one shared shape, two entry points.
 */
export function planLimitFor(usage: PlanUsage | null, axis: PlanAxis): PlanLimitInfo | null {
  if (usage === null || !isAtCap(usage, axis)) return null;
  const row = usage.axes[axis]!;
  return { axis, limit: row.limit, current: row.current, plan: usage.plan };
}
